import { useCallback, useEffect, useState } from "react";
import { addDays } from "date-fns";
import { Plus, Plane, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmt, ymd, daysBetween } from "@/lib/dates";
import { HOLIDAY, HOLIDAY_STATUS } from "@/lib/colors";
import { PageHeader, Pill, Empty } from "@/components/common";
import { HolidayDialog } from "@/components/holidays/HolidayDialog";
import { Button } from "@/components/ui/button";

const RequestRow = ({ h, onCancel }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-4 border-b border-slate-100 last:border-0" data-testid={`holiday-row-${h.id}`}>
    <div className={`holiday-stripes w-11 h-11 rounded-xl border grid place-items-center shrink-0 ${HOLIDAY[h.type].cls}`}><Plane className="w-4 h-4" /></div>
    <div className="flex-1 min-w-0">
      <div className="font-bold text-slate-800">{fmt(h.start_date, "EEE d MMM")} – {fmt(h.end_date, "EEE d MMM yyyy")} <span className="text-slate-400 font-normal">· {daysBetween(h.start_date, h.end_date)} day(s)</span></div>
      <div className="text-sm text-slate-500">{HOLIDAY[h.type].label}{h.note ? ` · ${h.note}` : ""}</div>
      {h.comment && <div className="text-sm text-slate-700 mt-1" data-testid={`holiday-comment-${h.id}`}>“{h.comment}” — {h.reviewed_by_name}</div>}
    </div>
    <div className="flex items-center gap-2">
      <Pill cls={HOLIDAY_STATUS[h.status].cls} testid={`holiday-status-${h.id}`}>{HOLIDAY_STATUS[h.status].label}</Pill>
      {h.status === "pending" && <Button size="sm" variant="ghost" onClick={() => onCancel(h)} data-testid={`holiday-cancel-${h.id}`}><X className="w-4 h-4 mr-1" /> Cancel</Button>}
    </div>
  </div>
);

export default function Holidays() {
  const [mine, setMine] = useState([]);
  const [teamOff, setTeamOff] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const start = ymd(new Date());
    const [a, b] = await Promise.all([api.get("/holidays"), api.get("/calendar", { params: { start, end: ymd(addDays(new Date(), 60)) } })]);
    setMine(a.data);
    setTeamOff(b.data.holidays.sort((x, y) => x.start_date.localeCompare(y.start_date)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cancel = async (h) => {
    try {
      await api.delete(`/holidays/${h.id}`);
      toast.success("Request cancelled");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader title="Holidays" subtitle="Request time off and see who's away so nobody gets double-booked.">
        <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="new-holiday-btn"><Plus className="w-4 h-4 mr-1.5" /> Request leave</Button>
      </PageHeader>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6">
        <section>
          <h2 className="text-base md:text-lg font-bold text-slate-800 mb-3">My requests</h2>
          {mine.length ? (
            <div className="rounded-2xl border border-slate-200 bg-white" data-testid="my-holiday-list">{mine.map((h) => <RequestRow key={h.id} h={h} onCancel={cancel} />)}</div>
          ) : <Empty testid="my-holiday-empty">No requests yet.</Empty>}
        </section>
        <section>
          <h2 className="text-base md:text-lg font-bold text-slate-800 mb-3">Who's off · next 60 days</h2>
          <div className="rounded-2xl border border-slate-200 bg-white p-2 space-y-1" data-testid="team-off-list">
            {teamOff.map((h) => (
              <div key={h.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50">
                <span className={`holiday-stripes w-2.5 h-9 rounded-full border ${HOLIDAY[h.type].cls}`} />
                <div className="leading-tight">
                  <div className="font-bold text-sm text-slate-800">{h.user_name}</div>
                  <div className="text-xs text-slate-500">{fmt(h.start_date, "d MMM")} – {fmt(h.end_date, "d MMM")} · {HOLIDAY[h.type].label}</div>
                </div>
              </div>
            ))}
            {teamOff.length === 0 && <div className="text-sm text-slate-500 px-3 py-6 text-center">Nobody is off.</div>}
          </div>
        </section>
      </div>
      <HolidayDialog open={open} onClose={() => setOpen(false)} onSaved={load} />
    </div>
  );
}
